import {useEffect, useRef, useState} from 'react'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import CompleteProfileForm from '@/customComponents/uiComponents/global/CompleteProfileForm.jsx'
import AddCardPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PaymentMethodsPopup.jsx'
import TripRouteMap from '@/components/map/TripRouteMap.jsx'
import {hopnLanguageArray} from '@/utils/functions.js'
import {dummyMap, peoplesIcon, fiveStars, verifiedBadge} from '@img/index.js'

/**
 * THE RESERVE RIDE FLOW, in one modal — what used to be two sections on the page.
 *
 *   profile     name / email / phone the booking gate still wants (only when something is
 *               missing — see CompleteProfileForm)
 *   card        add the card the ride is charged to (Secure Payment). Skipped when the
 *               passenger already has cards — they pick one on the page, see `skipCard`
 *   processing  the map + loader the booking flow shows while it submits, and it is what
 *               actually calls `doConfirm`
 *
 * …and when the confirm lands the modal closes and the page's own booking-received deck
 * (SuccessPopup) takes over — the same creative view the confirm pages show.
 *
 * The step list is fixed when the modal opens (the parent re-keys it on every open): the
 * account refetch that follows a saved field must not drop the step the passenger is on.
 *
 * ── Why the processing step waits ───────────────────────────────────────────────────────
 * Same handshake as the booking flow's processingBooking: once the confirm succeeds the
 * step stays for at least MIN, until the map's intro animation reports in, and never past
 * MAX — so a map that never reports can't strand a booking that has already gone through.
 */
const MIN_PROCESSING_MS = 8000
const MAX_PROCESSING_MS = 12000

export default function ReserveRideModal({isOpen, onClose, onDone, e, missing, onTerms, skipCard = false}) {
    const [steps, setSteps] = useState(() => [...(missing.length ? ['profile'] : []), ...(skipCard ? [] : ['card']), 'processing'])
    const [profileMissing, setProfileMissing] = useState(missing)
    const [index, setIndex] = useState(0)
    const step = steps[index]

    // processing
    const startedRef = useRef(false)
    const introDoneRef = useRef(false)
    const pendingAdvanceRef = useRef(null)
    const timersRef = useRef([])
    const [settled, setSettled] = useState(false)   // the confirm call has come back

    const advance = () => setIndex((i) => Math.min(i + 1, steps.length - 1))

    // Entering `processing` is what books the ride. The ref keeps StrictMode's double effect
    // from confirming twice.
    // ⚠️ `isOpen` matters: this component is mounted for the page's whole life, and when the
    // step list is just ['processing'] (card already picked on the page, nothing missing on
    // the profile) that IS step 0 — so without the guard a page load confirmed the trip.
    useEffect(() => {
        if (!isOpen || step !== 'processing' || startedRef.current) return
        startedRef.current = true
        setSettled(false)
        Promise.resolve(e.doConfirm()).finally(() => setSettled(true))
    }, [isOpen, step]) // eslint-disable-line react-hooks/exhaustive-deps

    // Confirmed → hold for the map, then hand over to the booking-received deck.
    useEffect(() => {
        if (step !== 'processing' || !e.showSuccess) return undefined
        const finish = () => onDone()
        const hasAnimatedMap = !!e.route?.polyline
        timersRef.current.push(setTimeout(() => {
            if (introDoneRef.current || !hasAnimatedMap) finish()
            else pendingAdvanceRef.current = finish
        }, MIN_PROCESSING_MS))
        timersRef.current.push(setTimeout(() => {
            if (pendingAdvanceRef.current) {
                pendingAdvanceRef.current = null
                finish()
            }
        }, MAX_PROCESSING_MS))
        return () => timersRef.current.splice(0).forEach(clearTimeout)
    }, [step, e.showSuccess]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleIntroDone = () => {
        introDoneRef.current = true
        const pending = pendingAdvanceRef.current
        if (pending) {
            pendingAdvanceRef.current = null
            pending()
        }
    }

    const failed = step === 'processing' && settled && !e.showSuccess
    // The confirm was refused for the profile — send them back to fill it in.
    const refusedForProfile = failed && Array.isArray(e.identityMissing) && e.identityMissing.length > 0

    const retry = () => {
        startedRef.current = false
        setSettled(false)
        if (refusedForProfile) {
            const wanted = e.identityMissing
            e.setIdentityMissing(null)
            setProfileMissing(wanted)
            setSteps(['profile', ...(skipCard ? [] : ['card']), 'processing'])
            setIndex(0)
        } else {
            // No card step when the passenger picked a card on the page — back to the page.
            const cardAt = steps.indexOf('card')
            if (cardAt < 0) return onClose()
            setIndex(cardAt)
        }
    }

    // ── processing ──────────────────────────────────────────────────────────────
    if (step === 'processing') {
        const route = e.route
        return (
            <Modal isOpen={isOpen} onClose={() => {}} flushBody flushX flushTop className="md:max-w-157.5">
                <div className="over-right-area">
                    {route?.polyline
                        ? <TripRouteMap polyline={route.polyline} legs={route.legs}
                                        animate minimal onIntroDone={handleIntroDone}/>
                        : <img src={dummyMap} alt="Map"/>}
                </div>

                <div className="hopn-booking-processing modalPages-spacing padding-bottom-30">
                    {failed ? (
                        <>
                            <div className="booking-error">
                                <h6>Please check following errors</h6>
                                {refusedForProfile
                                    ? 'Your profile is incomplete — we need a few details before you can book.'
                                    : (e.bookingError || 'We could not complete your booking. Please try again.')}
                            </div>
                            <button type="button" onClick={retry} className="hopn-add-passenger-button mt-6">
                                {refusedForProfile ? 'Complete your profile' : 'Back'}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="circle-rotate">
                                <img decoding="async" src={peoplesIcon} alt="People"/>
                            </div>

                            <span className="title">
                                {hopnLanguageArray('ride_confirmed', 'title', 'We are processing your booking…')}
                            </span>

                            <strong>
                                {hopnLanguageArray('ride_confirmed', 'description', 'Over 8 million km of airport transfers by pro partners in 20 years')}
                            </strong>

                            <div className="text-center pt-5 flex flex-col items-center gap-3">
                                <img decoding="async" src={fiveStars} alt="Five stars"/>
                                <img decoding="async" src={verifiedBadge} alt="Verified"/>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        )
    }

    // ── card ────────────────────────────────────────────────────────────────────
    // Straight to Secure Payment (the add-card form) — only for a passenger with NO saved
    // cards. One who has cards picks one on the page instead (see `skipCard`).
    if (step === 'card') {
        return (
            <AddCardPopup isOpen={isOpen} onClose={onClose} closeOnAdded={false}
                          onCardAdded={(c) => {
                              e.setSavedCards((prev) => [...prev, c])
                              e.handleCardSelect(c.id)
                              advance()
                          }}/>
        )
    }

    // ── profile ──────────────────────────────────────────────────────────
    return (
        <Modal isOpen={isOpen} onClose={onClose} className="md:max-w-157.5">
            <ModalHeader title="" onBack={onClose}/>

            {step === 'profile' && (
                <CompleteProfileForm missing={profileMissing} onCompleted={advance} onTerms={onTerms}/>
            )}
        </Modal>
    )
}
